import { css, html, LitElement, PropertyValues } from "lit";
import { customElement, property, queryAssignedElements } from "lit/decorators.js";

import { Map, View } from "ol";
import MapBrowserEvent from "ol/MapBrowserEvent";
import { Coordinate } from "ol/coordinate";
import TileLayer from "ol/layer/Tile";
import { fromLonLat } from "ol/proj";
import OSM from "ol/source/OSM";

import olStyles from "ol/ol.css" with { type: "css" };
import type { OlLayer, OlLayerReadyEvent, OlLayerRemovedEvent } from "./layers/ol-layer";
import type Layer from "ol/layer/Layer";
import { Extent, getCenter } from "ol/extent";
import BaseEvent from "ol/events/Event";
import {
    OlMapContextMenuEvent,
    OlMapKeyboardEvent,
    OlMapPointerEvent,
    type OlMapContextMenuEventDetail,
    type OlMapKeyboardEventDetail,
    type OlMapKeyboardEventType,
    type OlMapPointerEventDetail,
    type OlMapPointerEventType,
} from "./ol-map-events";

@customElement("ol-map")
export class OlMap extends LitElement {
    static styles = [
        olStyles,
        css`
            :host {
                display: block;
                height: 100%;
                width: 100%;
            }
            #map {
                width: 100%;
                height: 100%;
            }
            slot {
                display: none;
            }
        `,
    ];

    private _map?: Map;
    private _extentFitPending = true;
    private _resizeObserver?: ResizeObserver;

    @property({
        type: Number
    })
    zoom: number = 2;

    @property({
        type: Array
    })
    center: Coordinate | undefined;

    @property({
        type: Array
    })
    extent: Extent | undefined;

    @queryAssignedElements({
        slot: '',
        flatten: true,
    })
    private _slottedLayers!: OlLayer<Layer>[];

    
    protected render(): unknown {
        return html`
            <div id="map"></div>

            <slot
                @slotchanged=${this._onLayerSlotChanged}
                @ol-layer-ready=${this._onLayerReady}
                @ol-layer-removed=${this._onLayerRemoved}
            ></slot>
        `;
    }

    private _onViewChanged(event: BaseEvent) {
        const view = event.target as View;

        const zoom = view.getZoom();
        const center = view.getCenter();

        if (zoom && this.zoom !== zoom) {
            this.zoom = zoom;
        }

        if (center && (!this.center || this.center[0] !== center[0] || this.center[1] !== center[1])) {
            this.center = center;
        }
    }

    protected getUpdatedViewInstance(changedProperties?: PropertyValues<this>): View {
        let extent: Extent | undefined;
        let center: Coordinate | undefined;
        let zoom: number;

        // extent
        if ((!changedProperties || changedProperties.has("extent")) && this.extent && Array.isArray(this.extent) && this.extent.length === 4) {
            extent = this.extent;
        }

        // center
        if ((!changedProperties || changedProperties.has("center")) && this.center && Array.isArray(this.center) && this.center.length === 2) {
            center = this.center;
        } else if (extent) {
            center = this.center = getCenter(extent);
        } else {
            center = this.center = [0, 0];
        }

        // zoom
        if (!changedProperties || changedProperties.has("zoom")) {
            zoom = this.zoom ?? 0;
        } else {
            zoom = this.zoom = 0;
        }

        // view creation
        let view: View | undefined = this._map?.getView();

        if (!view || extent) {
            if (view) {
                view.un("change", this._onViewChanged);
                view.dispose();
            }

            // recreation is required
            view = new View({
                extent,
                zoom,
                center,
            });

            view.on("change", this._onViewChanged);
        } else {
            if (zoom) {
                view.setZoom(zoom);
            }

            if (center) {
                view.setCenter(center);
            }
        }

        return view;
    }

    protected firstUpdated() {
        const mapRef = this.shadowRoot?.getElementById('map') as HTMLElement;

        const view = this.getUpdatedViewInstance()

        this._map = new Map({
            target: mapRef,
            layers: [],
            view,
            keyboardEventTarget: mapRef,
        });

        this._resizeObserver = new ResizeObserver(() => this._fitExtentToViewport());
        this._resizeObserver.observe(mapRef);
        requestAnimationFrame(() => this._fitExtentToViewport());

        this._map.on("pointermove", () => {
            this._map?.getTargetElement().style.removeProperty("cursor");
        });
        // OpenLayers emits these additional pointer events at runtime, although
        // its Map.on declaration currently omits them from MapBrowserEventType.Types.
        this._map.on(["click", "singleclick", "dblclick", "pointerdown", "pointerup", "pointercancel", "pointermove", "pointerdrag"] as never, (event: Event | BaseEvent) => {
            if (!(event instanceof MapBrowserEvent)) {
                return;
            }

            this._dispatchPointerEvent(event.type as OlMapPointerEventType, event);
        });
        mapRef.tabIndex = 0;
        mapRef.addEventListener("keydown", this._onKeyDown);
        mapRef.addEventListener("keyup", this._onKeyUp);
        mapRef.addEventListener("contextmenu", this._onContextMenu);
        this._registerSlottedLayers();
    }

    private _dispatchPointerEvent(type: OlMapPointerEventType, event: MapBrowserEvent): void {
        if (!this._map) {
            return;
        }

        const hitTolerance = this._getHitTolerance();
        const detail: OlMapPointerEventDetail = {
            type,
            event,
            features: this._map.getFeaturesAtPixel(event.pixel, { hitTolerance }),
            hitTolerance,
        };
        const publicEvent = new OlMapPointerEvent(detail);

        if (this.dispatchEvent(publicEvent)) {
            for (const layer of this._slottedLayers) {
                if (layer.getPointerEventTypes().includes(type)) {
                    layer.handlePointerEvent(detail);
                }
            }
        }
    }

    private readonly _onKeyDown = (event: KeyboardEvent): void => {
        this._dispatchKeyboardEvent("keydown", event);
    };

    private readonly _onKeyUp = (event: KeyboardEvent): void => {
        this._dispatchKeyboardEvent("keyup", event);
    };

    private _dispatchKeyboardEvent(type: OlMapKeyboardEventType, event: KeyboardEvent): void {
        const detail: OlMapKeyboardEventDetail = { type, event };
        const publicEvent = new OlMapKeyboardEvent(detail);

        if (this.dispatchEvent(publicEvent)) {
            for (const layer of this._slottedLayers) {
                if (layer.getKeyboardEventTypes().includes(type)) {
                    layer.handleKeyboardEvent(detail);
                }
            }
        }
    }

    private readonly _onContextMenu = (event: MouseEvent): void => {
        if (!this._map) {
            return;
        }

        const pixel = this._map.getEventPixel(event);
        const hitTolerance = this._getHitTolerance();
        const detail: OlMapContextMenuEventDetail = {
            type: "contextmenu",
            event,
            pixel,
            coordinate: this._map.getCoordinateFromPixel(pixel),
            features: this._map.getFeaturesAtPixel(pixel, { hitTolerance }),
            hitTolerance,
        };
        const publicEvent = new OlMapContextMenuEvent(detail);

        if (this.dispatchEvent(publicEvent)) {
            for (const layer of this._slottedLayers) {
                if (layer.getContextMenuEventTypes().includes("contextmenu")) {
                    layer.handleContextMenuEvent(detail);
                }
            }
        }
    };

    private _getHitTolerance(): number {
        return typeof window !== "undefined" &&
            (window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0)
            ? 18
            : 0;
    }

    private _fitExtentToViewport(): void {
        if (!this._map || !this.extent || !this._extentFitPending) {
            return;
        }

        this._map.updateSize();
        const size = this._map.getSize();
        if (!size || size[0] <= 0 || size[1] <= 0) {
            return;
        }

        this._map.getView().fit(this.extent, { size });
        this._extentFitPending = false;
    }

    protected update(changedProperties: PropertyValues<this>): void {
        super.update(changedProperties);

        if (!this._map) {
            return;
        }

        const view = this.getUpdatedViewInstance(changedProperties);
        this._map.setView(view);

        if (changedProperties.has("extent")) {
            this._extentFitPending = true;
            requestAnimationFrame(() => this._fitExtentToViewport());
        }
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();

        this._resizeObserver?.disconnect();
        this._resizeObserver = undefined;
        this._map?.setTarget(undefined);
    }

    private _registerSlottedLayers() {
        if (!this._map) {
            return;
        }

        const layers = this._map.getLayers();
        const layersArray = layers.getArray();

        for (const slottedLayer of this._slottedLayers) {
            const olLayer = slottedLayer.getLayer();

            if (olLayer && !layersArray.includes(olLayer)) {
                layers.push(olLayer);
            }
        }
    }

    private _onLayerSlotChanged(): void {
        this._registerSlottedLayers();
    }

    private _onLayerReady(event: OlLayerReadyEvent): void {
        const layer = event.layer;

        if (this._map && layer) {
            this._map.addLayer(layer);
        }
    }

    private _onLayerRemoved(event: OlLayerRemovedEvent): void {
        const layer = event.layer;

        if (this._map && layer) {
            this._map.removeLayer(layer);
        }
    }
}
